import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { vendorInitiationsAPI } from '../services/api';
import type { User } from '../types';
import { hasAnyVmsRole, hasVmsRole } from '../utils/vmsRole';

export interface VendorInitiationSummary {
  id: number;
  service_name: string | null;
  status: string;
}

function storageIdKey(userId: number): string {
  return `vms_selected_vendor_initiation_id_${userId}`;
}

function storageCacheKey(userId: number): string {
  return `vms_selected_vendor_initiation_cache_${userId}`;
}

/** Drop persisted picks on login/logout only (not on page refresh). */
function clearPersistedWorkspaceSelection(userId: number | undefined): void {
  if (userId == null) return;
  try {
    localStorage.removeItem(storageIdKey(userId));
    localStorage.removeItem(storageCacheKey(userId));
    sessionStorage.removeItem(storageIdKey(userId));
    sessionStorage.removeItem(storageCacheKey(userId));
  } catch {
    /* ignore */
  }
}

function readPersistedWorkspace(userId: number): {
  id: number | null;
  summary: VendorInitiationSummary | null;
} {
  try {
    const rawId = localStorage.getItem(storageIdKey(userId));
    if (!rawId) return { id: null, summary: null };
    const id = Number(rawId);
    if (!Number.isInteger(id) || id < 1) return { id: null, summary: null };

    let summary: VendorInitiationSummary | null = null;
    const rawCache = localStorage.getItem(storageCacheKey(userId));
    if (rawCache) {
      const parsed = JSON.parse(rawCache) as VendorInitiationSummary;
      if (parsed?.id === id) summary = parsed;
    }
    return { id, summary };
  } catch {
    return { id: null, summary: null };
  }
}

function persistWorkspaceSelection(
  userId: number,
  id: number | null,
  summary: VendorInitiationSummary | null
): void {
  try {
    if (id == null) {
      localStorage.removeItem(storageIdKey(userId));
      localStorage.removeItem(storageCacheKey(userId));
      return;
    }
    localStorage.setItem(storageIdKey(userId), String(id));
    if (summary?.id === id) {
      localStorage.setItem(storageCacheKey(userId), JSON.stringify(summary));
    } else {
      localStorage.removeItem(storageCacheKey(userId));
    }
  } catch {
    /* ignore */
  }
}

/** Clear workspace service filter — call on login/logout, not on refresh. */
export function clearVmsWorkspacePersistence(userId?: number): void {
  if (userId != null) {
    clearPersistedWorkspaceSelection(userId);
    return;
  }
  try {
    const prefixes = ['vms_selected_vendor_initiation_id_', 'vms_selected_vendor_initiation_cache_'];
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Show the VMS “active service” dropdown: business owners and invoice admins scope by approved
 * initiations they own / all; stakeholders by assigned flow; BH/Legal/DPO by approver assignment.
 */
export function shouldShowVmsWorkspacePicker(user: User | null | undefined): boolean {
  if (!user) return false;
  if (hasVmsRole(user, 'admin') && !hasAnyVmsRole(user, ['business_owner', 'business_head', 'stakeholder', 'legal', 'dpo']))
    return false;
  if (hasVmsRole(user, 'business_owner') || hasVmsRole(user, 'stakeholder')) return true;
  if (hasAnyVmsRole(user, ['business_head', 'legal', 'dpo'])) return true;
  const p = user.invoice_permission;
  return p === 'admin' || p === 'super_admin';
}

type VmsWorkspaceContextValue = {
  selectedVendorInitiationId: number | null;
  /** Set selection in memory; pass null to clear (e.g. All Services). */
  setSelectedVendorInitiationId: (id: number | null) => void;
  /** Select id and refresh summary from API. */
  selectVendorInitiation: (id: number) => Promise<void>;
  cachedSummary: VendorInitiationSummary | null;
  refreshCachedSummary: () => Promise<void>;
  workspacePickerVisible: boolean;
  approvedOptions: { id: number; service_name: string | null; status: string }[];
  optionsLoading: boolean;
  reloadApprovedOptions: () => Promise<void>;
  /** False until workspace selection state is initialized for the current user. */
  workspaceStorageHydrated: boolean;
};

const VmsWorkspaceContext = createContext<VmsWorkspaceContextValue | null>(null);

export function VmsWorkspaceProvider({
  children,
  user,
}: {
  children: React.ReactNode;
  user: User | null | undefined;
}): React.ReactElement {
  const userId = user?.id;
  const pickerVisible = shouldShowVmsWorkspacePicker(user ?? null);

  const [selectedVendorInitiationId, setSelectedState] = useState<number | null>(null);
  const [cachedSummary, setCachedSummary] = useState<VendorInitiationSummary | null>(null);
  const [approvedOptions, setApprovedOptions] = useState<
    { id: number; service_name: string | null; status: string }[]
  >([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [workspaceStorageHydrated, setWorkspaceStorageHydrated] = useState(false);

  useEffect(() => {
    setWorkspaceStorageHydrated(false);
  }, [userId, pickerVisible]);

  useEffect(() => {
    if (!userId || !pickerVisible) {
      setSelectedState(null);
      setCachedSummary(null);
      setWorkspaceStorageHydrated(true);
      return;
    }
    const persisted = readPersistedWorkspace(userId);
    setSelectedState(persisted.id);
    setCachedSummary(persisted.summary);
    setWorkspaceStorageHydrated(true);
  }, [userId, pickerVisible]);

  useEffect(() => {
    if (!userId || !pickerVisible || !workspaceStorageHydrated) return;
    persistWorkspaceSelection(userId, selectedVendorInitiationId, cachedSummary);
  }, [userId, pickerVisible, workspaceStorageHydrated, selectedVendorInitiationId, cachedSummary]);

  const fetchSummary = useCallback(
    async (id: number): Promise<VendorInitiationSummary | null> => {
      try {
        const data = (await vendorInitiationsAPI.getById(id)) as {
          id?: number;
          service_name?: string | null;
          status?: string;
        };
        if (data?.id == null) return null;
        return {
          id: Number(data.id),
          service_name: data.service_name ?? null,
          status: String(data.status ?? ''),
        };
      } catch {
        return null;
      }
    },
    []
  );

  const refreshCachedSummary = useCallback(async () => {
    const id = selectedVendorInitiationId;
    if (id == null) {
      setCachedSummary(null);
      return;
    }
    const s = await fetchSummary(id);
    if (s) {
      setCachedSummary(s);
    }
  }, [selectedVendorInitiationId, fetchSummary]);

  useEffect(() => {
    if (!pickerVisible || selectedVendorInitiationId == null) return;
    // `selectVendorInitiation` / URL sync already fetched; avoid a second identical getById.
    if (cachedSummary?.id === selectedVendorInitiationId) return;
    let cancelled = false;
    (async () => {
      const s = await fetchSummary(selectedVendorInitiationId);
      if (cancelled || !s) return;
      setCachedSummary(s);
    })();
    return () => {
      cancelled = true;
    };
  }, [pickerVisible, selectedVendorInitiationId, fetchSummary, cachedSummary?.id]);

  const setSelectedVendorInitiationId = useCallback((id: number | null) => {
    setSelectedState(id);
    if (id == null) {
      setCachedSummary(null);
    }
  }, []);

  const selectVendorInitiation = useCallback(
    async (id: number) => {
      setSelectedState(id);
      const s = await fetchSummary(id);
      if (s) {
        setCachedSummary(s);
      } else {
        setCachedSummary({ id, service_name: null, status: 'unknown' });
      }
    },
    [fetchSummary]
  );

  const reloadApprovedOptions = useCallback(async () => {
    if (!pickerVisible) return;
    setOptionsLoading(true);
    try {
      type Row = { id: number; service_name?: string | null; status?: string };
      let rows: Row[] = [];
      if (hasVmsRole(user, 'stakeholder') && !hasVmsRole(user, 'business_owner')) {
        const res = await vendorInitiationsAPI.listMyStakeholderInitiations({ page: 1, limit: 100 });
        rows = (res as { data?: Row[] })?.data ?? [];
      } else if (
        hasAnyVmsRole(user, ['business_head', 'legal', 'dpo']) &&
        !hasVmsRole(user, 'business_owner')
      ) {
        const res = await vendorInitiationsAPI.listMyAssignedApproverInitiations({ page: 1, limit: 100 });
        rows = (res as { data?: Row[] })?.data ?? [];
      } else {
        const res = await vendorInitiationsAPI.list({
          status: 'approved',
          limit: 100,
          page: 1,
        });
        rows =
          (res as { success?: boolean; data?: Row[] })?.data ?? [];
      }
      setApprovedOptions(
        rows.map((r) => ({
          id: r.id,
          service_name: r.service_name ?? null,
          status: String(r.status ?? ''),
        }))
      );
    } catch {
      setApprovedOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  }, [pickerVisible, user]);

  useEffect(() => {
    if (!pickerVisible) return;
    void reloadApprovedOptions();
  }, [pickerVisible, reloadApprovedOptions]);

  const value = useMemo<VmsWorkspaceContextValue>(
    () => ({
      selectedVendorInitiationId,
      setSelectedVendorInitiationId,
      selectVendorInitiation,
      cachedSummary,
      refreshCachedSummary,
      workspacePickerVisible: pickerVisible,
      approvedOptions,
      optionsLoading,
      reloadApprovedOptions,
      workspaceStorageHydrated,
    }),
    [
      selectedVendorInitiationId,
      setSelectedVendorInitiationId,
      selectVendorInitiation,
      cachedSummary,
      refreshCachedSummary,
      pickerVisible,
      approvedOptions,
      optionsLoading,
      reloadApprovedOptions,
      workspaceStorageHydrated,
    ]
  );

  return <VmsWorkspaceContext.Provider value={value}>{children}</VmsWorkspaceContext.Provider>;
}

export function useVmsWorkspace(): VmsWorkspaceContextValue {
  const ctx = useContext(VmsWorkspaceContext);
  if (!ctx) {
    throw new Error('useVmsWorkspace must be used within VmsWorkspaceProvider');
  }
  return ctx;
}

/** Safe hook for pages that may render outside VMS (returns null). */
export function useVmsWorkspaceOptional(): VmsWorkspaceContextValue | null {
  return useContext(VmsWorkspaceContext);
}
